import type { Directive, DirectiveBinding } from "vue";

export type NumberFormatType = "compact" | "comma" | "plain" | "indian" | "hybrid";

interface NumberFormatBinding extends DirectiveBinding {
  value: NumberFormatType;
  arg?: NumberFormatType;
}

/**
 * Vue directive for formatting numbers in different styles
 *
 * Usage:
 * v-number-format="'compact'"    -> 4K, 1.2M, 1.5B
 * v-number-format="'comma'"      -> 4,000, 1,200,000
 * v-number-format="'plain'"      -> 4000, 1200000
 * v-number-format="'indian'"     -> 4,000, 12,00,000
 * v-number-format="'hybrid'"     -> 4,000 (≤9999), 10K, 1.2M (>9999)
 * v-number-format:compact        -> 4K, 1.2M, 1.5B (using arg)
 *
 * If content is not a number, it displays as-is
 */
export const numberFormatDirective: Directive = {
  mounted(el: HTMLElement, binding: NumberFormatBinding) {
    // Store the initial content and format it
    el.dataset.originalContent = el.textContent || "";
    formatElement(el, binding);
  },

  updated(el: HTMLElement, binding: NumberFormatBinding) {
    // On update, check if the content is different from our last formatted value
    const currentContent = el.textContent || "";
    const lastFormattedValue = el.dataset.lastFormattedValue || "";

    // If the current content is different from what we last formatted,
    // it means Vue updated the content with new reactive data
    if (currentContent !== lastFormattedValue) {
      el.dataset.originalContent = currentContent;
    }

    formatElement(el, binding);
  }
};

/**
 * Safely parse a string as a number, handling comma-separated numbers
 * and rejecting mixed content to maintain "display as-is" behavior
 */
const safeParseNumber = (input: string): number | null => {
  if (!input || typeof input !== "string") {
    return null;
  }

  const trimmed = input.trim();

  // Handle empty or whitespace-only strings
  if (!trimmed) {
    return null;
  }

  // Check if the string is purely numeric (allowing commas, decimal points, and negative sign)
  // This regex matches: optional minus, digits with optional commas every 3 digits, optional decimal part
  const pureNumberRegex = /^-?(\d{1,3}(,\d{3})*|\d+)(\.\d+)?$/;

  if (!pureNumberRegex.test(trimmed)) {
    return null;
  }

  // Remove commas and parse
  const withoutCommas = trimmed.replace(/,/g, "");
  const parsed = parseFloat(withoutCommas);

  // Final validation - ensure the parsed number is valid and finite
  if (isNaN(parsed) || !isFinite(parsed)) {
    return null;
  }

  return parsed;
};

const formatElement = (el: HTMLElement, binding: NumberFormatBinding) => {
  const originalContent = el.dataset.originalContent || el.textContent || "";

  // Store original content if not already stored (handle empty string case)
  if (!el.dataset.originalContent && el.dataset.originalContent !== "") {
    el.dataset.originalContent = originalContent;
  }

  // Get format type from binding value or arg
  const formatType = binding.value || binding.arg || "comma";

  // Parse number more safely
  const numericValue = safeParseNumber(originalContent.trim());

  // If not a valid number, show original content
  if (numericValue === null) {
    el.textContent = originalContent;
    return;
  }

  // Apply formatting based on type
  let formattedValue: string;

  switch (formatType) {
    case "compact":
      formattedValue = numericValue.toLocaleString("en-US", {
        maximumFractionDigits: 1,
        notation: "compact",
        compactDisplay: "short"
      });
      break;

    case "comma":
      formattedValue = numericValue.toLocaleString("en-US");
      break;

    case "plain":
      formattedValue = numericValue.toString();
      break;

    case "indian":
      formattedValue = numericValue.toLocaleString("en-IN");
      break;

    case "hybrid":
      if (numericValue <= 9999) {
        // Use comma formatting for numbers up to 9999
        formattedValue = numericValue.toLocaleString("en-US");
      } else {
        // Use compact notation for numbers above 9999
        formattedValue = numericValue.toLocaleString("en-US", {
          maximumFractionDigits: 1,
          notation: "compact",
          compactDisplay: "short"
        });
      }
      break;

    default:
      formattedValue = numericValue.toLocaleString("en-US");
  }

  el.textContent = formattedValue;

  // Store the formatted value so we can detect changes in the next update
  el.dataset.lastFormattedValue = formattedValue;
};

export default numberFormatDirective;
